import {Injectable} from '@angular/core';
import {ThemeDTO} from '../../shared/models/theme.model';
import {Observable, of, switchMap} from 'rxjs';
import {BrandingContact, BrandingLegal} from '../../shared/models/branding.model';
import {BrandingService} from '../api/branding.service';
import {Title} from '@angular/platform-browser';
import {GlobalErrorService} from './global-error.service';
import {ClientConfigService} from './client-config.service';
import {environment} from '../../../environments/environment';
import {catchError, map} from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class ThemeService {
  private readonly fallbackBrandingCode = 'hoomvip';
  private readonly fallbackTitle = 'Hoomvip';

  private currentTheme: ThemeDTO = {};
  private currentBrandingCode: string | null = null;
  private manifestObjectUrl: string | null = null;

  constructor(
    private brandingService: BrandingService,
    private titleService: Title,
    private globalErrorService: GlobalErrorService,
    private clientConfigService: ClientConfigService,
  ) {
  }

  getQrPath(): string {
    return this.currentTheme.qrPath || environment.defaultHost;
  }

  getLogoPath(): string {
    return this.currentTheme.pathLogo || '';
  }

  getTileOrder(): string[] {
    return this.currentTheme.tileOrder || [];
  }

  getLegal(): Observable<BrandingLegal> {
    return this.getResolvedBrandingCode().pipe(
      switchMap(brandingCode => this.brandingService.getBrandingLegal(brandingCode)),
      catchError(error => {
        console.error('Error loading branding legal:', error);
        this.globalErrorService.handleUnexpectedError(error);
        return of(this.getFallbackLegal());
      })
    );
  }

  getContact(): Observable<BrandingContact> {
    return this.getResolvedBrandingCode().pipe(
      switchMap(brandingCode => this.brandingService.getBrandingContact(brandingCode)),
      catchError(error => {
        console.error('Error loading branding contact:', error);
        this.globalErrorService.handleUnexpectedError(error);
        return of(this.getFallbackContact());
      })
    );
  }

  private getResolvedBrandingCode(): Observable<string> {
    if (this.currentBrandingCode) {
      return of(this.currentBrandingCode);
    }

    return this.clientConfigService.getThemeForCurrentDomain().pipe(
      map(theme => theme.brandingCode ?? this.fallbackBrandingCode)
    );
  }

  private getFallbackLegal(): BrandingLegal {
    const emptyLegalUrls = {
      terms_url: '',
      privacy_url: '',
    };

    return {
      es: emptyLegalUrls,
      en: emptyLegalUrls,
      fr: emptyLegalUrls,
    };
  }

  private getFallbackContact(): BrandingContact {
    return {
      email: null,
      phone: null,
      whatsapp: null,
      web: null,
    };
  }

  detectAndApplyTheme(): void {
    this.clientConfigService.getSpecialClientRedirectUrl().pipe(
      switchMap(redirectUrl => {
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return of(null);
        }

        return this.clientConfigService.getThemeForCurrentDomain();
      })
    ).subscribe({
      next: theme => {
        if (!theme) {
          return;
        }

        this.applyThemeDTO(theme);
      },
      error: error => {
        console.error('Error loading client configuration:', error);
        this.globalErrorService.handleUnexpectedError(error);
      }
    });
  }

  applyThemeDTO(baseTheme: ThemeDTO): void {
    this.currentBrandingCode = baseTheme.brandingCode ?? this.fallbackBrandingCode;

    this.brandingService.getBrandingTheme(this.currentBrandingCode).subscribe({
      next: brandingTheme => {
        this.applyTheme({
          ...baseTheme,
          ...brandingTheme,
        });
      },
      error: error => {
        console.error('Error loading branding theme:', error);
        this.globalErrorService.handleUnexpectedError(error);
        this.applyTheme(baseTheme);
      }
    });
  }

  applyTheme(theme: ThemeDTO): void {
    this.currentTheme = {
      ...this.currentTheme,
      ...theme
    };

    const map: Record<string, string | undefined> = {
      '--component-color': this.currentTheme.componentColor,
      '--component-color-secondary': this.currentTheme.componentColorLight,
      '--text-color': this.currentTheme.textColor,
      '--text-color-secondary': this.currentTheme.textColorLight,
      '--background-image': this.currentTheme.pathBackground
        ? `url('${this.currentTheme.pathBackground}')`
        : `url('/images/home-hoomvip.png')`,
    };

    const root = document.documentElement;
    Object.entries(map).forEach(([k, v]) => {
      if (v) {
        root.style.setProperty(k, v);
      }
    });

    this.titleService.setTitle(this.currentTheme.title || this.fallbackTitle);

    this.applyIcons(this.currentTheme);
  }

  private applyIcons(theme: ThemeDTO): void {
    const icons = theme.icons;
    if (!icons) {
      return;
    }

    this.setLinkHref('app-favicon-main', this.toAbsoluteUrl(icons.favicon));
    this.setLinkHref('app-favicon-png', this.toAbsoluteUrl(icons.favicon96));
    this.setLinkHref('app-favicon-svg', this.toAbsoluteUrl(icons.faviconSvg || icons.favicon));
    this.setLinkHref('app-favicon-ico', this.toAbsoluteUrl(icons.faviconIco || icons.favicon));
    this.setLinkHref('app-apple-touch-icon', this.toAbsoluteUrl(icons.appleTouchIcon));
    this.setDynamicManifest(theme);
  }

  private setLinkHref(id: string, href: string): void {
    const link = document.getElementById(id) as HTMLLinkElement | null;
    if (!link) {
      return;
    }
    link.href = href;
  }

  private toAbsoluteUrl(url: string): string {
    try {
      return new URL(url, window.location.origin).toString();
    } catch {
      return url;
    }
  }

  private setDynamicManifest(theme: ThemeDTO): void {
    const link = document.getElementById('app-manifest') as HTMLLinkElement | null;
    const icons = theme.icons;

    if (!link || !icons) {
      return;
    }

    if (this.manifestObjectUrl) {
      URL.revokeObjectURL(this.manifestObjectUrl);
      this.manifestObjectUrl = null;
    }

    console.log('start url: ', this.getManifestStartUrl());

    const manifest = {
      name: theme.title || this.fallbackTitle,
      short_name: theme.title || this.fallbackTitle,
      start_url: this.getManifestStartUrl(),
      scope: this.getManifestScope(),
      icons: [
        {
          src: this.toAbsoluteUrl(icons.manifest192),
          sizes: '192x192',
          type: 'image/png',
          purpose: 'maskable',
        },
        {
          src: this.toAbsoluteUrl(icons.manifest512),
          sizes: '512x512',
          type: 'image/png',
          purpose: 'maskable',
        },
      ],
      theme_color: '#ffffff',
      background_color: theme.componentColor || '#E63656',
      display: 'standalone',
    };

    const blob = new Blob([JSON.stringify(manifest)], {
      type: 'application/manifest+json',
    });

    this.manifestObjectUrl = URL.createObjectURL(blob);
    link.href = this.manifestObjectUrl;
  }

  private getManifestStartUrl(): string {
    const brandingCodeFromUrl = this.getBrandingCodeFromUrl();
    const startUrl = new URL('/', window.location.origin);

    if (brandingCodeFromUrl) {
      startUrl.searchParams.set(environment.brandingCodeQueryParam, brandingCodeFromUrl);
    }

    return startUrl.toString();
  }

  private getManifestScope(): string {
    return new URL('/', window.location.origin).toString();
  }

  private getBrandingCodeFromUrl(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get(environment.brandingCodeQueryParam)?.trim() || null;
  }
}
